/**
 * FAX-DROPZONE-001 (Nov 2026) — premium drag & drop upload for the returned fax
 * document. Presentation + upload transport only: same endpoint
 * (POST /orders/{id}/fax/received), same FormData fields, same 15MB / type rules.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, AlertCircle, FolderOpen, RefreshCw, Upload, X, FileText } from 'lucide-react';
import './fax-dropzone.css';

const ACCEPTED_EXTS = ['.pdf', '.png', '.jpg', '.jpeg', '.tif', '.tiff'];
const ACCEPT_ATTR = ACCEPTED_EXTS.join(',');
const MAX_BYTES = 15 * 1024 * 1024;

const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const validate = (file) => {
  const ext = `.${(file.name.split('.').pop() || '').toLowerCase()}`;
  if (!ACCEPTED_EXTS.includes(ext)) return `Unsupported file type "${ext}". Accepted: PDF, PNG, JPG, TIFF.`;
  if (file.size > MAX_BYTES) return `File too large (${fmtSize(file.size)}). Maximum size is 15 MB.`;
  return null;
};

const FaxIcon = ({ size = 'md' }) => (
  <span className={`fxd-icon fxd-icon--${size}`} aria-hidden="true">
    <span className="fxd-icon__ring" />
    <span className="fxd-icon__doc">
      <span className="fxd-icon__tag">FAX</span>
      <span className="fxd-icon__line" /><span className="fxd-icon__line" /><span className="fxd-icon__line fxd-icon__line--short" />
    </span>
    <span className="fxd-icon__badge"><Upload className="w-3.5 h-3.5" strokeWidth={3} /></span>
  </span>
);

export const FaxResponseDropzone = ({ uploadUrl, faxEntryId, onSuccess, onCancel, onDone }) => {
  const [phase, setPhase] = useState('idle'); // idle | selected | uploading | success | error
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const busyRef = useRef(false);
  const dragDepth = useRef(0);

  // Dropping a file outside the zone must never navigate the browser to the file.
  useEffect(() => {
    const block = (e) => { e.preventDefault(); };
    window.addEventListener('dragover', block);
    window.addEventListener('drop', block);
    return () => { window.removeEventListener('dragover', block); window.removeEventListener('drop', block); };
  }, []);

  const pick = useCallback((f) => {
    if (!f) return;
    const msg = validate(f);
    if (msg) { setFile(null); setError(msg); setPhase('error'); return; }
    setFile(f); setError(''); setProgress(0); setPhase('selected');
  }, []);

  const openPicker = () => { if (!busyRef.current && inputRef.current) inputRef.current.click(); };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    dragDepth.current = 0; setDragOver(false);
    if (busyRef.current) return;
    pick(e.dataTransfer?.files?.[0]);
  };
  const onDragEnter = (e) => { e.preventDefault(); dragDepth.current += 1; if (!busyRef.current) setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); dragDepth.current = Math.max(0, dragDepth.current - 1); if (dragDepth.current === 0) setDragOver(false); };
  const onDragOver = (e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = busyRef.current ? 'none' : 'copy'; };

  const upload = () => {
    if (!file || busyRef.current) return;
    busyRef.current = true; setPhase('uploading'); setProgress(0); setError('');
    const fd = new FormData();
    fd.append('file', file);
    if (faxEntryId) fd.append('fax_entry_id', faxEntryId);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', uploadUrl);
    xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);
    xhr.upload.onprogress = (ev) => { if (ev.lengthComputable) setProgress(Math.min(99, Math.round((ev.loaded / ev.total) * 100))); };
    xhr.onerror = () => { busyRef.current = false; setError('Network error — the file could not be uploaded.'); setPhase('error'); };
    xhr.onload = () => {
      busyRef.current = false;
      let data = {};
      try { data = JSON.parse(xhr.responseText || '{}'); } catch { /* non-JSON body */ }
      if (xhr.status >= 200 && xhr.status < 300 && data.success) {
        setProgress(100); setResult(data); setPhase('success');
        onSuccess?.(data);
        setTimeout(() => onDone?.(data), 2200);
      } else {
        setError(data.detail || data.error || `Upload failed (HTTP ${xhr.status}). Please try again.`);
        setPhase('error');
      }
    };
    xhr.send(fd);
  };

  const reset = () => { setFile(null); setError(''); setProgress(0); setPhase('idle'); if (inputRef.current) inputRef.current.value = ''; };

  const zoneClass = `fxd-zone${dragOver ? ' is-dragover' : ''}${phase !== 'idle' ? ' is-compact' : ''}`;

  return (
    <div className="fxd" data-testid="fax-received-upload-area" data-phase={phase}>
      <div className="fxd-head">
        <p className="fxd-title">Upload returned fax document</p>
        {phase !== 'uploading' && phase !== 'success' && (
          <button type="button" onClick={onCancel} className="fxd-close" aria-label="Close upload panel" data-testid="fax-received-cancel-btn"><X className="w-3.5 h-3.5" /></button>
        )}
      </div>

      <input ref={inputRef} type="file" accept={ACCEPT_ATTR} className="sr-only" tabIndex={-1}
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} data-testid="fax-received-file-input" />

      {(phase === 'idle' || phase === 'error' && !file) && (
        <div className={zoneClass} role="button" tabIndex={0} aria-label="Drag and drop the fax response here, or press Enter to browse files"
          onClick={openPicker} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPicker(); } }}
          onDrop={onDrop} onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={onDragOver} data-testid="fax-dropzone">
          <FaxIcon />
          <div className="fxd-copy">
            {dragOver ? (
              <p className="fxd-headline fxd-headline--drop" data-testid="fax-dropzone-release-text">Release to Upload Fax Response</p>
            ) : (
              <>
                <p className="fxd-headline" data-testid="fax-dropzone-headline">Drag &amp; Drop Fax Response Here</p>
                <p className="fxd-sub">or click to browse files · PDF, PNG, JPG, TIFF · up to 15 MB</p>
                <button type="button" className="fxd-browse" onClick={(e) => { e.stopPropagation(); openPicker(); }} data-testid="fax-browse-files-btn">
                  <FolderOpen className="w-4 h-4" /> Browse Files
                </button>
                {phase === 'error' && (
                  <p className="fxd-inline-error" role="alert" data-testid="fax-dropzone-validation-error"><AlertCircle className="w-3.5 h-3.5" /> {error}</p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {phase === 'selected' && file && (
        <div className="fxd-card fxd-card--selected" data-testid="fax-dropzone-selected">
          <FaxIcon size="sm" />
          <div className="fxd-card__body">
            <p className="fxd-card__title"><FileText className="w-3.5 h-3.5" /> <span className="truncate" data-testid="fax-dropzone-filename">{file.name}</span> <span className="fxd-muted">({fmtSize(file.size)})</span></p>
            <p className="fxd-card__hint">Ready to upload. This marks the fax as received for this recipient.</p>
          </div>
          <div className="fxd-card__actions">
            <button type="button" className="fxd-primary" onClick={upload} data-testid="fax-received-upload-btn"><Upload className="w-3.5 h-3.5" /> Upload &amp; Mark Received</button>
            <button type="button" className="fxd-ghost" onClick={reset} data-testid="fax-dropzone-remove-btn">Remove</button>
          </div>
        </div>
      )}

      {phase === 'uploading' && file && (
        <div className="fxd-card fxd-card--uploading" aria-live="polite" aria-busy="true" data-testid="fax-dropzone-uploading">
          <FaxIcon size="sm" />
          <div className="fxd-card__body">
            <p className="fxd-card__title">Uploading: <strong className="truncate">{file.name}</strong></p>
            <div className="fxd-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
              <span className="fxd-progress__bar" style={{ width: `${Math.max(4, progress)}%` }} />
            </div>
          </div>
          <span className="fxd-pct" data-testid="fax-dropzone-progress">{progress}%</span>
        </div>
      )}

      {phase === 'success' && file && (
        <div className="fxd-card fxd-card--success" role="status" data-testid="fax-dropzone-success">
          <span className="fxd-check" aria-hidden="true"><CheckCircle2 className="w-6 h-6" strokeWidth={2.5} /></span>
          <div className="fxd-card__body">
            <p className="fxd-card__title fxd-card__title--ok">Fax Response Uploaded Successfully</p>
            <p className="fxd-card__hint"><FileText className="w-3.5 h-3.5" /> {file.name} <span className="fxd-muted">({fmtSize(file.size)})</span>{result?.message ? ` · ${result.message}` : ''}</p>
          </div>
        </div>
      )}

      {phase === 'error' && file && (
        <div className="fxd-card fxd-card--error" role="alert" data-testid="fax-dropzone-error">
          <span className="fxd-bang" aria-hidden="true"><AlertCircle className="w-6 h-6" strokeWidth={2.5} /></span>
          <div className="fxd-card__body">
            <p className="fxd-card__title fxd-card__title--err">Upload Failed</p>
            <p className="fxd-card__hint" data-testid="fax-dropzone-error-message">{error}</p>
            <p className="fxd-card__hint fxd-muted"><FileText className="w-3.5 h-3.5" /> {file.name} ({fmtSize(file.size)}) — kept for retry</p>
          </div>
          <div className="fxd-card__actions">
            <button type="button" className="fxd-retry" onClick={upload} data-testid="fax-dropzone-retry-btn"><RefreshCw className="w-3.5 h-3.5" /> Retry</button>
            <button type="button" className="fxd-ghost" onClick={reset} data-testid="fax-dropzone-choose-another-btn">Choose another</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FaxResponseDropzone;
